// Central media + copy constants — all real studio photography.
export const STUDIO_INFO = {
  name: "Millionare Mir Studio",
  address: {
    line1: "6910 Stenton Ave",
    line2: "Philadelphia, PA 19150",
  },
  phoneDisplay: "(267) 972-0165",
  phoneTel: "+12679720165",
  emailPrimary: "Amirwallace24@gmail.com",
  emailStudio: "millionaremir30@gmail.com",
};

// Real studio photos uploaded by the client.
// Semantic slots preserve existing component imports.
export const MEDIA = {
  // Founder / on-location with camera rig at sunset — signature hero image.
  heroBackground:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/p619xrgg_IMG_0603.jpeg",
  // Recording studio desk with monitors (moody purple) — background texture.
  textureBg:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/esu8gdw8_IMG_0594.jpeg",
  // Artist in vocal booth being filmed (purple lighting) — music portrait.
  portfolio1:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/8mssrd0e_IMG_0598.jpeg",
  // Founder / on-location — used as About founder shot + Cinematic Video service.
  portfolio2:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/p619xrgg_IMG_0603.jpeg",
  // Microphone close-up in purple booth — intimate detail shot.
  portfolio3:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/v0mm4bxp_IMG_0597.jpeg",
  // Recording studio setup (desk, monitors, gaming chair).
  portfolio4:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/esu8gdw8_IMG_0594.jpeg",
  // Professional photo/video studio with cyclorama + lighting rig.
  serviceFilm:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/g85z8cbe_5F45AF89-CABD-4607-A31B-B7AC00BCCBCD.png",
  // Product photography setup — camera on tripod, softbox, kitchen product styling.
  productSetup:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/ggfubj28_IMG_0607.jpeg",
  // Behind-the-scenes POV through Viltrox monitor framing a snow-covered red car.
  btsMonitor:
    "https://customer-assets.emergentagent.com/job_visual-impact-55/artifacts/i7e677nb_IMG_0608.jpeg",
};

export const SERVICES = [
  {
    id: "music-photography",
    number: "01",
    title: "Music Photography",
    tagline: "Portraits that hit harder than the track.",
    description:
      "Raw, authentic photoshoots built for rollout covers, press kits, and tour visuals. Studio, location, and editorial — shot with an eye for mood, not trends.",
    deliverables: [
      "Editorial portrait direction",
      "Album & single cover art",
      "Press & tour photography",
      "Retouching & color grade",
    ],
    image: MEDIA.portfolio1,
  },
  {
    id: "cinematic-video",
    number: "02",
    title: "Cinematic Video",
    tagline: "Motion with weight. Stories with edge.",
    description:
      "High-energy music videos, performance films, and short-form social edits. Lighting, camera movement, and pace engineered to elevate the record, not decorate it.",
    deliverables: [
      "Music video treatments",
      "Performance & live films",
      "Short-form social cutdowns",
      "Color & sound design",
    ],
    image: MEDIA.portfolio2,
  },
  {
    id: "film-production",
    number: "03",
    title: "Film Production",
    tagline: "Full-scale visual rollouts, end to end.",
    description:
      "Concept through post — documentaries, brand films, and artist-led narratives. Creative direction, production management, and a crew built to execute at an industry level.",
    deliverables: [
      "Concept & creative direction",
      "Full production management",
      "Crew, gear & locations",
      "Post, VFX & delivery",
    ],
    image: MEDIA.serviceFilm,
  },
  {
    id: "recording-studio",
    number: "04",
    title: "Recording Studio",
    tagline: "A room built for performing artists.",
    description:
      "A private recording studio in Philadelphia for performing artists. Vocal booth, mixing suite, and a session-ready environment — walk in with a record, walk out with a master. Block time or full artist development packages.",
    deliverables: [
      "Tracking & vocal sessions",
      "Mixing & mastering",
      "Beat production rooms",
      "Hourly & lockout rates",
    ],
    image: MEDIA.portfolio4,
  },
];

// Galleries pull from the 7 real photos, rotated per case study.
const GALLERY = {
  neon: [MEDIA.portfolio1, MEDIA.portfolio3, MEDIA.portfolio4, MEDIA.btsMonitor],
  rollout: [MEDIA.portfolio2, MEDIA.serviceFilm, MEDIA.productSetup, MEDIA.portfolio4],
  silent: [MEDIA.portfolio3, MEDIA.portfolio1, MEDIA.portfolio4, MEDIA.portfolio2],
  live: [MEDIA.portfolio4, MEDIA.portfolio1, MEDIA.btsMonitor, MEDIA.serviceFilm],
  alexa: [MEDIA.serviceFilm, MEDIA.portfolio2, MEDIA.productSetup, MEDIA.portfolio4],
  product: [MEDIA.productSetup, MEDIA.serviceFilm, MEDIA.portfolio4, MEDIA.portfolio2],
  whiteout: [MEDIA.btsMonitor, MEDIA.portfolio2, MEDIA.serviceFilm, MEDIA.portfolio4],
};

export const PORTFOLIO = [
  {
    id: "neon-confessions",
    title: "Neon Confessions",
    subtitle: "Press campaign & single art for an emerging hip-hop artist.",
    category: "Music Photography",
    year: "2025",
    client: "Kairo West — Fade Label",
    location: "Brooklyn, NY",
    duration: "2-day shoot",
    image: MEDIA.portfolio1,
    span: "md:col-span-7 md:row-span-2",
    story: [
      "Kairo came to us two weeks before a single drop with a visual that had to feel like 2 a.m. on a rooftop in the city — honest, loud, and a little unhinged.",
      "We built a single-light studio day paired with a night-one location shoot in Bushwick. The goal: a cover image plus a full press kit, DSP banners, and a teaser stills sequence.",
      "The final cover ran across all DSPs, and the supporting portraits carried the rollout through three months of press.",
    ],
    deliverables: [
      "Single cover artwork",
      "15 press portraits",
      "DSP banner set",
      "Teaser stills reel",
    ],
    credits: [
      { role: "Creative Direction", name: "Mir Wallace" },
      { role: "Photography", name: "Millionare Mir Studio" },
      { role: "Stylist", name: "Jade Oboh" },
      { role: "Retouch", name: "Studio Post" },
    ],
    gallery: GALLERY.neon,
  },
  {
    id: "midnight-rollout",
    title: "Midnight Rollout",
    subtitle: "Cinematic music video for a breakout R&B record.",
    category: "Cinematic Video",
    year: "2025",
    client: "Lena Vox",
    location: "Philadelphia, PA",
    duration: "1-day shoot, 2-week post",
    image: MEDIA.portfolio2,
    span: "md:col-span-5",
    story: [
      "Lena's record lived in the space between a confession and a warning. We wanted motion that felt like the camera was holding its breath — long lenses, slow pushes, warm practicals only.",
      "Shot on ARRI Alexa Mini with vintage Zeiss primes. Color grade leaned into amber highlights with deep cyan shadow separation.",
      "The video crossed a quarter million views in its first week and earned a feature on a major editorial playlist.",
    ],
    deliverables: [
      "3-minute music video",
      "Vertical social cutdowns",
      "BTS stills set",
      "Final color & audio mix",
    ],
    credits: [
      { role: "Director", name: "Mir Wallace" },
      { role: "Director of Photography", name: "Millionare Mir Studio" },
      { role: "Editor", name: "Studio Post" },
      { role: "Colorist", name: "Studio Post" },
    ],
    gallery: GALLERY.rollout,
  },
  {
    id: "silent-room",
    title: "Silent Room",
    subtitle: "Intimate performance film captured in a single take.",
    category: "Cinematic Video",
    year: "2024",
    client: "North Lane",
    location: "Philadelphia, PA",
    duration: "Half-day shoot",
    image: MEDIA.portfolio3,
    span: "md:col-span-5",
    story: [
      "No edits, no cuts — just one camera, one performer, one room with practical lighting. The brief was: make it feel like you weren't supposed to be there.",
      "We ran a slider dolly across 18 feet of track, timing the push to the song's key changes. The whole performance was captured in the second take.",
      "The film became the artist's most-watched piece on YouTube and was used as the opener for their debut tour.",
    ],
    deliverables: [
      "4-minute single-take film",
      "Stereo audio mix",
      "Thumbnail & poster art",
    ],
    credits: [
      { role: "Director", name: "Mir Wallace" },
      { role: "Cinematographer", name: "Millionare Mir Studio" },
      { role: "Sound", name: "Studio Post" },
    ],
    gallery: GALLERY.silent,
  },
  {
    id: "live-from-the-floor",
    title: "Live From The Floor",
    subtitle: "Full concert documentary across two nights of sold-out shows.",
    category: "Film Production",
    year: "2024",
    client: "Uptown Records",
    location: "New York City, NY",
    duration: "2 nights live, 4-week post",
    image: MEDIA.portfolio4,
    span: "md:col-span-7",
    story: [
      "Three cameras, two nights, one hour of final cut. The label wanted a concert film that didn't feel like a concert film — more like a backstage novel with the stage as one of the chapters.",
      "We ran an A-cam on a Steadicam, a locked-off B-cam on the mezzanine, and a handheld C-cam moving from the pit to the green room. Audio was pulled clean from the board with ambient room mics layered on top.",
      "Delivered as a long-form film plus five shorter edits for social. The project has since been referenced in two separate industry case studies.",
    ],
    deliverables: [
      "60-minute concert film",
      "5 short-form cutdowns",
      "Stills library (400+)",
      "Stereo & 5.1 mixes",
    ],
    credits: [
      { role: "Director", name: "Mir Wallace" },
      { role: "Producer", name: "Millionare Mir Studio" },
      { role: "Cinematography", name: "Studio Crew" },
      { role: "Edit & Color", name: "Studio Post" },
    ],
    gallery: GALLERY.live,
  },
  {
    id: "alexa-35mm",
    title: "Alexa / 35mm",
    subtitle: "Gear-forward brand film for a boutique camera rental house.",
    category: "Film Production",
    year: "2025",
    client: "Confidential",
    location: "Los Angeles, CA",
    duration: "3-day production",
    image: MEDIA.serviceFilm,
    span: "md:col-span-12",
    story: [
      "The client wanted a film that didn't sell cameras — it sold craft. We built three vignettes: a portrait shoot, a practical lighting test, and a handheld dolly-in on a still life.",
      "Every shot was designed to flex a piece of gear without ever cutting to a product shot. The camera is always the subject and never the subject.",
      "Won a regional short-form craft award the following quarter and is now used as the rental house's anchor brand film.",
    ],
    deliverables: [
      "90-second brand film",
      "3 vignette cutdowns",
      "Print campaign stills",
      "Award-grade master deliverables",
    ],
    credits: [
      { role: "Director", name: "Mir Wallace" },
      { role: "Cinematographer", name: "Millionare Mir Studio" },
      { role: "Producer", name: "Studio" },
    ],
    gallery: GALLERY.alexa,
  },
  {
    id: "product-light",
    title: "Product Light",
    subtitle: "Tabletop product campaign — home goods & culinary brands.",
    category: "Film Production",
    year: "2025",
    client: "Independent Home Brand",
    location: "Philadelphia, PA",
    duration: "1-day tabletop shoot",
    image: MEDIA.productSetup,
    span: "md:col-span-6",
    story: [
      "The client needed a fast, modern product campaign — cutting boards, cookware, and counter appliances — shot with the same cinematic sensibility we bring to artist work.",
      "We built a compact tabletop rig: hard-key softbox with a negative fill, a secondary rim light, and a DSLR on a locked-off tripod for plate shots plus a gimbal for motion b-roll.",
      "Delivered a 30-shot stills library, a hero campaign image, and a 20-second motion piece cut for paid social.",
    ],
    deliverables: [
      "30 stills (hero + variants)",
      "20s motion cutdown",
      "White & lifestyle plates",
      "Retouched masters",
    ],
    credits: [
      { role: "Creative Direction", name: "Mir Wallace" },
      { role: "Photography", name: "Millionare Mir Studio" },
      { role: "Lighting", name: "Studio Crew" },
    ],
    gallery: GALLERY.product,
  },
  {
    id: "white-out",
    title: "White-Out",
    subtitle: "Automotive short — shot through a blizzard, on a monitor.",
    category: "Cinematic Video",
    year: "2025",
    client: "Independent Auto Feature",
    location: "Philadelphia, PA",
    duration: "Half-day shoot",
    image: MEDIA.btsMonitor,
    span: "md:col-span-6",
    story: [
      "A first-snow day in Philly turned into an opportunity. The red Honda looked like a single chord in a white chord chart — we grabbed the gimbal, a Viltrox monitor, and ran outside before the plows came through.",
      "Shot handheld on a stabilized rig with a warming filter to fight the cool daylight. Framed every move through the monitor so we could lock composition without losing feeling in our fingers.",
      "Cut into a 45-second short and a 10-second vertical — both performed at the top of the studio's social feed for the month.",
    ],
    deliverables: [
      "45s hero edit",
      "10s vertical cutdown",
      "BTS stills set",
      "Graded masters",
    ],
    credits: [
      { role: "Director / DP", name: "Mir Wallace" },
      { role: "Camera", name: "Millionare Mir Studio" },
      { role: "Post", name: "Studio Post" },
    ],
    gallery: GALLERY.whiteout,
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Mir doesn't just shoot — he directs the whole feeling. Every frame felt like the record.",
    name: "Kairo West",
    role: "Recording Artist",
  },
  {
    quote:
      "The kind of team that understands rollout. We got covers, press, and a short film from one campaign.",
    name: "Lena Vox",
    role: "Creative Director, Fade Label",
  },
  {
    quote:
      "Industry-level polish without the bloated agency process. They move fast and it shows.",
    name: "Marcus Ade",
    role: "Manager, North Lane",
  },
  {
    quote:
      "The video looked like a $200k budget. It wasn't. That's the Mir effect.",
    name: "Sienna Okafor",
    role: "Independent Artist",
  },
  {
    quote:
      "Storytelling, lighting, motion — they actually care about the artist, not the trend.",
    name: "Deon Hart",
    role: "A&R, Uptown Records",
  },
];

export const STATS = [
  { value: "120+", label: "Music Videos Shot" },
  { value: "40+", label: "Album Covers Delivered" },
  { value: "8", label: "Years of Craft" },
  { value: "15+", label: "Cities Lensed" },
];

export const PROCESS = [
  {
    step: "01",
    title: "Vision Call",
    body: "We sit with the artist, label, or manager. Map the record, the rollout, the references, the feeling.",
  },
  {
    step: "02",
    title: "Treatment",
    body: "Moodboards, shot lists, locations, wardrobe. A document the whole team can build from.",
  },
  {
    step: "03",
    title: "Production",
    body: "Crew, gear, and set dressed with intention. Every frame is composed, not captured by accident.",
  },
  {
    step: "04",
    title: "Post & Delivery",
    body: "Edit, color, sound, retouch. Delivered in every format the rollout needs — clean, tagged, on time.",
  },
];
